/* Shared product-page controller. All cart writes belong to the existing product form. */
(() => {
  'use strict';
  const root = document.getElementById('NexveyStickyPurchase');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';
  const $ = selector => root.querySelector(selector);
  const html = document.documentElement;
  const spacer = document.querySelector('[data-nsp-spacer]');
  const button = $('[data-nsp-submit]');
  const status = $('[data-nsp-status]');
  const quantityInput = $('[data-nsp-quantity]');
  const minus = $('[data-nsp-minus]');
  const plus = $('[data-nsp-plus]');
  let scope, form, source, variants = [], eventVariant, hasVariantEvent = false;
  let frame = 0, visible = false;
  let observer, sizeObserver;
  const clean = node => node ? node.textContent.trim().replace(/\s+/g, ' ') : '';
  const setText = (node, value) => { if (node.textContent !== value) node.textContent = value; };
  const shown = node => !!node && !!node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden';
  const money = cents => {
    if (window.theme?.Currency?.formatMoneyWithCurrency) {
      const text = document.createElement('span');
      text.innerHTML = window.theme.Currency.formatMoneyWithCurrency(cents, window.theme.settings.moneyFormat);
      return clean(text);
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:root.dataset.currency || 'USD'}).format(cents / 100);
  };
  function dimensions() {
    if (visible) html.style.setProperty('--nsp-height', `${Math.ceil(root.getBoundingClientRect().height)}px`);
  }
  function display(next) {
    if (next === visible) return;
    visible = next;
    root.hidden = !next;
    spacer.hidden = !next;
    html.classList.toggle('nsp-visible', next);
    dimensions();
  }
  function keyboardOpen() {
    const viewport = window.visualViewport;
    return viewport && !root.contains(document.activeElement) && viewport.scale === 1 && window.innerHeight - viewport.height > 150 &&
      document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
  }
  function overlayOpen() {
    if (document.fullscreenElement || html.classList.contains('js-drawer-open') || html.classList.contains('js-modal-open')) return true;
    return [...document.querySelectorAll('.modal--is-active, .drawer--is-open, dialog[open], [aria-modal="true"], [data-kl-scroll-locking-modal="true"]')].some(node => {
      if (!shown(node) || node.closest('[aria-hidden="true"], [inert]')) return false;
      const rect = node.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= innerHeight || rect.right <= 0 || rect.left >= innerWidth) return false;
      for (let parent=node; parent && parent!==document.body; parent=parent.parentElement) {
        if (getComputedStyle(parent).opacity === '0') return false;
      }
      return true;
    });
  }
  function currentVariant() {
    if (hasVariantEvent) return eventVariant;
    const field = form?.querySelector('[name="id"]');
    const selected = variants.find(v => String(v.id) === field?.value);
    if (selected) return selected;
    try { return JSON.parse(scope.querySelector('[data-current-variant-json]')?.value || 'null'); } catch { return null; }
  }
  function quantityState() {
    const field = form?.elements.namedItem('quantity') || scope?.querySelector('[name="quantity"]');
    const rule = currentVariant()?.quantity_rule || {};
    const min = Math.max(1, Number(rule.min) || 1, Number(field?.getAttribute('min')) || 1);
    const limits = [Number(rule.max), Number(field?.getAttribute('max'))].filter(n => n > 0);
    const max = limits.length ? Math.min(...limits) : Infinity;
    const step = Math.max(1, Number(rule.increment) || Number(field?.getAttribute('step')) || 1);
    const value = Math.max(min, Number.parseInt(field?.value, 10) || min);
    return {field, min, max, step, value};
  }
  function setQuantity(requested) {
    if (!form || quantityInput.disabled) return;
    const state = quantityState();
    const bounded = Math.max(state.min, Math.min(state.max, Number(requested) || state.min));
    const value = state.min + Math.floor((bounded - state.min) / state.step) * state.step;
    let field = state.field;
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden'; field.name = 'quantity';
      form.appendChild(field);
    }
    field.value = String(value);
    quantityInput.value = String(value);
    // Original add-on and accelerated-checkout listeners read the same quantity.
    field.dispatchEvent(new Event('input', {bubbles:true}));
    field.dispatchEvent(new Event('change', {bubbles:true}));
    status.hidden = true;
    schedule();
  }
  function sync() {
    if (!source?.isConnected || !scope?.isConnected || !shown(source)) { display(false); return; }
    const variant = currentVariant();
    const busy = source.classList.contains('btn--loading') || source.getAttribute('aria-busy') === 'true' || !!scope.querySelector('[data-nexvey-product-add-on].is-loading');
    const coupon = scope.querySelector('[data-nexvey-coupon-price]');
    const couponCode = coupon?.querySelector('[data-coupon-code]')?.getAttribute('data-coupon-code') || '';
    const activeCoupon = shown(coupon) && clean(coupon.querySelector('[data-ncp-coupon-price]')) && couponCode;
    const quantity = quantityState();
    const selection = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
    setText($('[data-nsp-selection]'), variant ? selection : 'Choose options');
    $('[data-nsp-selection]').hidden = !!variant && !selection;
    $('[data-nsp-change]').hidden = !scope.querySelector('.variant-wrapper');
    if (document.activeElement !== quantityInput) quantityInput.value = String(quantity.value);
    quantityInput.min = String(quantity.min);
    quantityInput.step = String(quantity.step);
    if (Number.isFinite(quantity.max)) quantityInput.max = String(quantity.max); else quantityInput.removeAttribute('max');
    quantityInput.disabled = busy || source.disabled || !variant;
    minus.disabled = quantityInput.disabled || quantity.value <= quantity.min;
    plus.disabled = quantityInput.disabled || quantity.value + quantity.step > quantity.max;
    const viewport = window.visualViewport;
    const keyboardOffset = viewport && viewport.scale === 1 && root.contains(document.activeElement) ? Math.max(0, innerHeight - viewport.height - viewport.offsetTop) : 0;
    root.style.bottom = `${keyboardOffset}px`;
    setText(button, busy ? 'Adding…' : clean(source.querySelector('[data-add-to-cart-text]') || source));
    button.disabled = busy || source.disabled;
    button.setAttribute('aria-busy', String(!!busy));
    // Read the rendered market-specific price/coupon block rather than recreating promotion rules.
    const price = activeCoupon ? coupon.querySelector('[data-ncp-coupon-price]') : scope.querySelector('[data-product-price]');
    setText($('[data-nsp-price]'), variant ? (clean(price) || money(variant.price)) : '—');
    const compare = $('[data-nsp-compare]');
    const beforeCoupon = activeCoupon ? clean(coupon.querySelector('[data-ncp-before-price]')) : '';
    compare.hidden = !variant || (activeCoupon ? !beforeCoupon || beforeCoupon === clean(price) : !(variant.compare_at_price > variant.price));
    if (!compare.hidden) setText(compare, activeCoupon ? beforeCoupon : money(variant.compare_at_price));
    const discounted = !!variant && (!!activeCoupon || variant.compare_at_price > variant.price);
    root.classList.toggle('nsp--discount', discounted);
    $('[data-nsp-discount-label]').hidden = !discounted;
    $('[data-nsp-price]').setAttribute('aria-label', activeCoupon ? `Price with coupon ${couponCode}: ${clean(price)}` : `Price: ${clean(price)}`);
    const image = $('[data-nsp-image]');
    let src = variant?.featured_image?.src;
    if (src) { const url = new URL(src, location.href); url.searchParams.set('width','160'); src = url.href; }
    if (src && image.getAttribute('src') !== src) { image.src = src; image.hidden = false; }
    // Only a button that has passed ABOVE the viewport activates the bar; never a button still below the fold.
    const top = source.getBoundingClientRect().bottom;
    const header = document.querySelector('#SiteHeader');
    const headerRect = header?.getBoundingClientRect();
    const headerBottom = headerRect && headerRect.top <= 0 && headerRect.bottom > 0 ? headerRect.bottom : 0;
    const threshold = visible ? headerBottom + 4 : headerBottom - 12;
    display(top < threshold && !overlayOpen() && !keyboardOpen());
    dimensions();
  }
  function schedule() {
    if (!frame) frame = requestAnimationFrame(() => { frame = 0; sync(); });
  }
  function review(target) {
    display(false);
    const field = target || scope.querySelector('[data-variant-input] input:not([disabled]), [data-variant-input] select, [name="quantity"], [data-add-to-cart]');
    const destination = field || source;
    const anchor = target || scope.querySelector('.variant-wrapper, .product__quantity') || source;
    anchor.scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block:'center'});
    destination.focus({preventScroll:true});
  }
  function onVariant(event) {
    hasVariantEvent = true;
    eventVariant = event.detail?.variant || null;
    status.hidden = true;
    schedule();
  }
  function onInvalid(event) { if (form.contains(event.target) || event.target.form === form) review(event.target); }
  function onError(event) {
    const error = event.detail?.errorMessage || event.detail?.description || event.detail?.error?.description || event.detail?.error?.message || event.detail?.error;
    setText(status, typeof error === 'string' ? error : 'Unable to add this item. Please review your options and try again.');
    status.hidden = false;
    schedule();
  }
  function onAdded() { status.hidden = true; schedule(); }
  function unbind() {
    observer?.disconnect(); sizeObserver?.disconnect();
    scope?.removeEventListener('variantChange', onVariant);
    form?.removeEventListener('ajaxProduct:added', onAdded);
    form?.removeEventListener('ajaxProduct:error', onError);
    form?.removeEventListener('invalid', onInvalid, true);
  }
  function bind() {
    unbind();
    // Limit to this page's product, excluding recommended products and quick views.
    scope = [...document.querySelectorAll(`.product-section[data-product-id="${root.dataset.productId}"]`)]
      .find(node => !node.closest('.modal, .drawer') && node.querySelector('form.product-single__form [data-add-to-cart]'));
    form = scope?.querySelector('form.product-single__form');
    source = form?.querySelector('[data-add-to-cart]');
    hasVariantEvent = false;
    if (!source) { display(false); return; }
    try { variants = JSON.parse(scope.querySelector('[data-variant-json]')?.value || '[]'); } catch { variants = []; }
    scope.addEventListener('variantChange', onVariant);
    form.addEventListener('invalid', onInvalid, true);
    form.addEventListener('ajaxProduct:added', onAdded);
    form.addEventListener('ajaxProduct:error', onError);
    observer = new MutationObserver(schedule);
    observer.observe(scope, {attributes:true,childList:true,characterData:true,subtree:true,attributeFilter:['disabled','class','value','aria-busy','hidden']});
    if (window.ResizeObserver) {
      sizeObserver = new ResizeObserver(schedule);
      sizeObserver.observe(scope); sizeObserver.observe(root);
    }
    schedule();
  }
  button.addEventListener('click', () => {
    setQuantity(quantityInput.value);
    sync();
    if (button.disabled || !source?.isConnected) return;
    status.hidden = true;
    if (!form.checkValidity()) { form.reportValidity(); return; }
    // click() retains the existing form submitter, add-on listeners and cart drawer behavior.
    source.click();
    schedule();
  });
  minus.addEventListener('click', () => setQuantity(quantityState().value - quantityState().step));
  $('[data-nsp-change]').addEventListener('click', () => review());
  plus.addEventListener('click', () => setQuantity(quantityState().value + quantityState().step));
  quantityInput.addEventListener('change', () => setQuantity(quantityInput.value));
  quantityInput.addEventListener('blur', () => setQuantity(quantityInput.value));
  document.addEventListener('change', event => {
    if (scope?.contains(event.target)) { status.hidden = true; schedule(); }
  });
  document.addEventListener('input', event => { if (scope?.contains(event.target)) schedule(); });
  document.addEventListener('scroll', schedule, {passive:true});
  window.addEventListener('resize', schedule, {passive:true});
  window.visualViewport?.addEventListener('resize', schedule, {passive:true});
  document.addEventListener('fullscreenchange', schedule);
  document.addEventListener('shopify:section:load', bind);
  document.addEventListener('shopify:section:unload', () => { if (!scope?.isConnected) { unbind(); display(false); } });
  const overlayObserver = new MutationObserver(records => {
    if (records.some(record => !root.contains(record.target) && record.target !== spacer)) schedule();
  });
  overlayObserver.observe(html, {attributes:true,attributeFilter:['class']});
  overlayObserver.observe(document.body, {attributes:true,attributeFilter:['class','style']});
  // Third-party dialogs can be inserted asynchronously or toggle aria/hidden without scrolling.
  overlayObserver.observe(document.body, {subtree:true,childList:true,attributes:true,attributeFilter:['aria-modal','aria-hidden','open','hidden','class','style']});
  bind();
})();
